import { FormEvent, useRef, useState } from "react";
import { ArrowRight, CalendarDays, Check, ChevronLeft, Clock3, MapPin, ShieldCheck } from "lucide-react";
import { ConceptBar } from "../components/ConceptBar";
import { FloatingShapes } from "../components/FloatingShapes";
import { MetricValue } from "../components/MetricValue";
import { useGsap } from "../hooks/useGsap";
import "../styles/govori.css";

type BookingData = {
  age: string;
  format: string;
  slot: string;
  parent: string;
  phone: string;
};

const initialBooking: BookingData = {
  age: "",
  format: "",
  slot: "",
  parent: "",
  phone: ""
};

export function GovoriPage() {
  const page = useRef<HTMLElement>(null);
  const [step, setStep] = useState(1);
  const [booking, setBooking] = useState<BookingData>(initialBooking);
  const [complete, setComplete] = useState(false);

  useGsap(page, (gsap) => {
    gsap.from(".govori-hero__copy > *", {
      opacity: 0,
      y: 26,
      stagger: 0.09,
      duration: 0.85,
      ease: "power3.out"
    });
    gsap.from(".govori-hero__media", {
      opacity: 0,
      scale: 0.92,
      duration: 1.1,
      ease: "back.out(1.2)"
    });
    gsap.from(".govori-method__card", {
      opacity: 0,
      y: 32,
      stagger: 0.08,
      duration: 0.7,
      ease: "power2.out",
      scrollTrigger: {
        trigger: ".govori-method__cards",
        start: "top 80%"
      }
    });
    gsap.fromTo(
      ".govori-session__media img",
      { scale: 1.12 },
      {
        scale: 1,
        scrollTrigger: {
          trigger: ".govori-session",
          start: "top bottom",
          end: "bottom top",
          scrub: true
        }
      }
    );
    gsap.to(".govori-hero__bubble", {
      y: (index) => (index % 2 ? -18 : 16),
      rotate: (index) => (index % 2 ? 7 : -6),
      duration: 2.6,
      stagger: 0.18,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut",
    });
    gsap.from(".govori-voice__wave i", {
      scaleY: 0.16,
      transformOrigin: "center",
      duration: 0.7,
      stagger: { each: 0.035, from: "center" },
      ease: "back.out(1.8)",
      scrollTrigger: { trigger: ".govori-voice", start: "top 76%" },
      onComplete: () => {
        gsap.to(".govori-voice__wave i", {
          scaleY: (index) => 0.72 + ((index * 17) % 27) / 100,
          duration: 0.9,
          stagger: { each: 0.035, from: "center" },
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
        });
      },
    });
    gsap.from(".govori-outcomes article, .govori-specialist__facts > div", {
      opacity: 0,
      y: 16,
      stagger: 0.04,
      duration: 0.5,
      ease: "power3.out",
      scrollTrigger: { trigger: ".govori-outcomes", start: "top 80%" },
    });
  });

  function nextStep() {
    if (step === 1 && !booking.age) return;
    if (step === 2 && (!booking.format || !booking.slot)) return;
    setStep(Math.min(3, step + 1));
  }

  function submitBooking(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!event.currentTarget.checkValidity()) {
      event.currentTarget.reportValidity();
      return;
    }
    setComplete(true);
  }

  return (
    <main id="main" ref={page} className="govori">
      <ConceptBar label="Детская логопедия" />

      <header className="govori-nav">
        <a className="govori-logo" href="#top">
          говори
        </a>
        <nav aria-label="Основная навигация">
          <a href="#method">Как занимаемся</a>
          <a href="#parents">Родителям</a>
          <a href="#faq">Вопросы</a>
        </nav>
        <a className="govori-nav__cta" href="#booking" data-magnetic>
          Записаться
        </a>
      </header>

      <section className="govori-hero" id="top" aria-labelledby="govori-title">
        <FloatingShapes className="govori-floating-shapes" />
        <div className="govori-hero__copy">
          <p>Логопедическая студия для детей 3–9 лет</p>
          <h1 id="govori-title">Говорить — это игра</h1>
          <p>
            Знакомимся без теста, играем без скучных повторений и постепенно переносим новые звуки
            в обычную речь
          </p>
          <div className="govori-hero__actions">
            <a className="govori-button" href="#booking" data-magnetic>
              Записаться на знакомство <ArrowRight aria-hidden="true" />
            </a>
            <a href="#method">Посмотреть, как всё проходит</a>
          </div>
          <div className="govori-hero__trust">
            <span>
              <ShieldCheck aria-hidden="true" /> Бережный формат
            </span>
            <span>
              <Clock3 aria-hidden="true" /> 30 или 45 минут
            </span>
          </div>
        </div>
        <div className="govori-hero__media" data-tilt>
          <img
            src="/assets/govori/playroom.webp"
            alt="Логопед и ребёнок играют с карточками на ковре"
            width="1693"
            height="929"
            fetchPriority="high"
          />
          <div className="govori-hero__bubble govori-hero__bubble--one">жа</div>
          <div className="govori-hero__bubble govori-hero__bubble--two">р-р-р</div>
          <div className="govori-hero__bubble govori-hero__bubble--three">ш</div>
        </div>
      </section>

      <section className="govori-fit">
        <p>Подойдёт, если ребёнок</p>
        <div className="govori-fit__marquee" aria-label="Ситуации, с которыми работает студия">
          <div>
            <span>пропускает звуки</span>
            <i>говорит очень быстро</i>
            <span>стесняется отвечать</span>
            <i>путает похожие звуки</i>
            <span>устаёт от занятий</span>
            <i>говорит тихо и неуверенно</i>
            <span>сложно пересказывает</span>
            <i>избегает длинных слов</i>
            <span>пропускает звуки</span>
            <i>говорит очень быстро</i>
            <span>стесняется отвечать</span>
            <i>путает похожие звуки</i>
            <span>устаёт от занятий</span>
            <i>говорит тихо и неуверенно</i>
            <span>сложно пересказывает</span>
            <i>избегает длинных слов</i>
          </div>
        </div>
      </section>

      <section className="govori-voice" aria-labelledby="govori-voice-title">
        <div className="govori-voice__copy">
          <p>Слышать прогресс</p>
          <h2 id="govori-voice-title" data-split>Речь меняется не по таблице. Она меняется в жизни.</h2>
          <p>Отмечаем не только отдельный звук, но и то, как ребёнок просит, спорит, рассказывает и сам начинает разговор.</p>
        </div>
        <div className="govori-voice__player">
          <div className="govori-voice__meta"><span>Неделя 01</span><b>Неделя 08</b></div>
          <div className="govori-voice__wave" aria-label="Условная аудиоволна прогресса речи">
            {[34, 58, 42, 76, 49, 88, 62, 39, 72, 96, 55, 81, 46, 68, 91, 61, 38, 75, 52, 84, 66, 44, 79, 57, 93, 64, 48, 72, 86, 54].map((height, index) => (
              <i key={index} style={{ height: `${height}%` }} />
            ))}
          </div>
          <div className="govori-voice__quote">«Я сам расскажу, что было в садике»</div>
        </div>
        <div className="govori-outcomes">
          <article data-spotlight><strong><MetricValue value={83} suffix="%" /></strong><span>детей начинают переносить звук в спонтанную речь</span></article>
          <article data-spotlight><strong><MetricValue value={8} suffix=" нед." /></strong><span>средний первый маршрут занятий</span></article>
          <article data-spotlight><strong><MetricValue value={4.9} decimals={1} /></strong><span>оценка родителей после первого курса</span></article>
        </div>
      </section>

      <section className="govori-method" id="method">
        <div className="govori-method__heading">
          <p>Как устроено занятие</p>
          <h2 data-split>Ребёнок играет. Специалист видит систему.</h2>
          <p>
            Каждая активность выглядит естественно, но решает конкретную задачу: дыхание,
            артикуляцию, слуховое внимание или перенос звука в спонтанную речь.
          </p>
        </div>
        <div className="govori-method__cards">
          <article className="govori-method__card govori-method__card--yellow" data-spotlight>
            <span>Сюжет</span>
            <h3>Сначала придумываем историю</h3>
            <p>Карточки и деревянные персонажи помогают заговорить без давления.</p>
          </article>
          <article className="govori-method__card govori-method__card--blue" data-spotlight>
            <span>Движение</span>
            <h3>Меняем позу и темп</h3>
            <p>Короткие игровые блоки удерживают внимание и не превращают встречу в урок.</p>
          </article>
          <article className="govori-method__card govori-method__card--orange" data-spotlight>
            <span>Родителям</span>
            <h3>Домой — одна понятная игра</h3>
            <p>Без пачки распечаток: только то, что легко встроить в обычный вечер.</p>
          </article>
        </div>
      </section>

      <section className="govori-session" id="parents">
        <div className="govori-session__media">
          <img
            src="/assets/govori/session.webp"
            alt="Руки ребёнка и специалиста раскладывают карточки и деревянные фигурки"
            width="1536"
            height="1024"
            loading="lazy"
          />
        </div>
        <div className="govori-session__copy">
          <p>Что получает родитель</p>
          <h2 data-split>Понимание вместо списка ошибок.</h2>
          <p>
            После знакомства специалист спокойно объясняет, что уже получается, где нужна помощь
            и какой формат даст ребёнку комфортный темп.
          </p>
          <ul>
            <li>
              <Check aria-hidden="true" />
              Короткое резюме встречи простым языком
            </li>
            <li>
              <Check aria-hidden="true" />
              План на 6–8 недель без обещаний «исправить всё»
            </li>
            <li>
              <Check aria-hidden="true" />
              Одна домашняя игра после каждого занятия
            </li>
          </ul>
        </div>
      </section>

      <section className="govori-specialist">
        <div className="govori-specialist__portrait">
          <img src="/assets/govori/therapist.webp" alt="Ведущий логопед студии Марина Левина" width="1200" height="1800" loading="lazy" />
          <span>Марина Левина / ведущий логопед</span>
        </div>
        <div className="govori-specialist__copy">
          <p>Люди, а не методички</p>
          <h2 data-split>Один специалист ведёт ребёнка от знакомства до устойчивой речи.</h2>
          <blockquote>«Моя задача — сделать так, чтобы новый звук был нужен ребёнку для игры, истории и настоящего разговора».</blockquote>
          <div className="govori-specialist__facts">
            <div><strong><MetricValue value={11} suffix=" лет" /></strong><span>практики</span></div>
            <div><strong><MetricValue value={640} suffix="+" /></strong><span>семей в работе</span></div>
            <div><strong>СПбГУ</strong><span>логопедия и нейропсихология</span></div>
          </div>
        </div>
      </section>

      <section className="govori-booking" id="booking" aria-labelledby="booking-title">
        <div className="govori-booking__intro">
          <p>Первое знакомство · 30 минут</p>
          <h2 id="booking-title" data-split>Выберите удобный формат.</h2>
          <p>
            Сначала знакомимся с ребёнком и запросом семьи, затем предлагаем понятный маршрут занятий.
          </p>
        </div>
        <div className="govori-wizard">
          <div className="govori-wizard__progress" aria-label={`Шаг ${step} из 3`}>
            {[1, 2, 3].map((item) => (
              <span key={item} className={item <= step ? "is-active" : ""}>
                {item}
              </span>
            ))}
          </div>

          {complete ? (
            <div className="govori-wizard__complete" role="status">
              <Check aria-hidden="true" />
              <h3>Знакомство забронировано.</h3>
              <p>
                {booking.format}, {booking.slot}. Подтверждение и памятка отправлены на указанный номер.
              </p>
              <button
                type="button"
                onClick={() => {
                  setBooking(initialBooking);
                  setStep(1);
                  setComplete(false);
                }}
              >
                Начать заново
              </button>
            </div>
          ) : (
            <>
              {step === 1 && (
                <fieldset>
                  <legend>Сколько лет ребёнку?</legend>
                  <div className="govori-options govori-options--age">
                    {["3–4", "5–6", "7–9"].map((age) => (
                      <button
                        type="button"
                        key={age}
                        className={booking.age === age ? "is-selected" : ""}
                        onClick={() => setBooking({ ...booking, age })}
                      >
                        {age} лет
                      </button>
                    ))}
                  </div>
                </fieldset>
              )}

              {step === 2 && (
                <fieldset>
                  <legend>Где и когда удобно?</legend>
                  <div className="govori-options">
                    {["В студии", "Онлайн"].map((format) => (
                      <button
                        type="button"
                        key={format}
                        className={booking.format === format ? "is-selected" : ""}
                        onClick={() => setBooking({ ...booking, format })}
                      >
                        {format === "В студии" ? <MapPin aria-hidden="true" /> : <CalendarDays aria-hidden="true" />}
                        {format}
                      </button>
                    ))}
                  </div>
                  <div className="govori-slots">
                    {["Вт, 17:30", "Ср, 12:00", "Сб, 10:30"].map((slot) => (
                      <button
                        type="button"
                        key={slot}
                        className={booking.slot === slot ? "is-selected" : ""}
                        onClick={() => setBooking({ ...booking, slot })}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                </fieldset>
              )}

              {step === 3 && (
                <form onSubmit={submitBooking}>
                  <fieldset>
                    <legend>Как с вами связаться?</legend>
                    <label>
                      <span>Имя родителя</span>
                      <input
                        type="text"
                        required
                        minLength={2}
                        autoComplete="name"
                        value={booking.parent}
                        onChange={(event) => setBooking({ ...booking, parent: event.target.value })}
                      />
                    </label>
                    <label>
                      <span>Телефон</span>
                      <input
                        type="tel"
                        required
                        autoComplete="tel"
                        value={booking.phone}
                        onChange={(event) => setBooking({ ...booking, phone: event.target.value })}
                      />
                    </label>
                    <button className="govori-wizard__submit" type="submit">
                      Подтвердить запись
                    </button>
                  </fieldset>
                </form>
              )}

              <div className="govori-wizard__nav">
                {step > 1 && (
                  <button type="button" onClick={() => setStep(step - 1)}>
                    <ChevronLeft aria-hidden="true" /> Назад
                  </button>
                )}
                {step < 3 && (
                  <button className="govori-wizard__next" type="button" onClick={nextStep}>
                    Дальше <ArrowRight aria-hidden="true" />
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="govori-faq" id="faq">
        <div>
          <p>Коротко о важном</p>
          <h2 data-split>Вопросы родителей</h2>
        </div>
        <div>
          <details open>
            <summary>Нужно ли готовить ребёнка к первой встрече?</summary>
            <p>Нет. Достаточно сказать, что вы идёте играть и знакомиться с новым взрослым.</p>
          </details>
          <details>
            <summary>Можно ли присутствовать на занятии?</summary>
            <p>На знакомстве — да. Дальше формат зависит от возраста и того, как ребёнку спокойнее.</p>
          </details>
          <details>
            <summary>Вы ставите диагноз?</summary>
            <p>Нет. Студия занимается педагогической поддержкой речи и при необходимости рекомендует врача.</p>
          </details>
        </div>
      </section>

      <footer className="govori-footer">
        <a className="govori-logo" href="#top">
          говори
        </a>
        <div>
          <p>Москва, Тихий переулок, 6</p>
          <a href="tel:+74950000000">+7 495 000-00-00</a>
        </div>
        <p>Центр детской речи · 2026</p>
      </footer>
    </main>
  );
}
